/**
 * Global toast notifications for async action feedback (§7g).
 *
 * Mounted once at App root. Toasts live in the Zustand store and are
 * pushed via `useStore((s) => s.pushToast)` directly:
 *
 *     const toast = useStore((s) => s.pushToast);
 *     toast("Project created", "success");
 *
 * Variants: "info" (default), "success", "error".
 *
 * Auto-dismissal is scheduled inside `pushToast` in the store (each
 * toast gets its own independent timer set at push time). This
 * component is now purely presentational — no effects, no timer
 * management — which avoids the previous bug where a burst of new
 * toasts would restart the dismissal timers on earlier ones, leaving
 * the oldest visible for N × TTL.
 *
 * Click-to-dismiss races the auto-timer: if the user clicks first,
 * the filter wins and the subsequent timer fires as a no-op.
 */

import { useStore } from "../store/useStore.js";

export default function ToastContainer() {
  const toasts = useStore((s) => s.toasts);
  const dismissToast = useStore((s) => s.dismissToast);

  if (toasts.length === 0) return null;

  return (
    <div className="toast-container" aria-live="polite" aria-atomic="false">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`toast toast-${toast.variant || "info"}`}
          onClick={() => dismissToast(toast.id)}
          role="status"
        >
          {toast.message}
        </div>
      ))}
    </div>
  );
}
